<template>
	<Spinner v-if="isSectionLoading" />
	<div v-show="!isSectionLoading">
		<!-- Customer Details -->
		<CheckoutDetails id="customerDetails" title="Customer">
			<template v-if="!showCustomerDetails" v-slot:email>
				<div id="customer-email-display" class="w-full mt-2 md:mt-0 md:pl-4 md:mx-4 text-sm text-ihh-midnight-gray flex items-center">
					{{ store?.user?.email || checkout.guest.email }}
				</div>
			</template>

			<template v-if="customerComplete || store?.user?.id" v-slot:button>
				<div v-if="store?.user?.id">
					<Login v-slot="{ logout }" small="true" class="!p-0">
						<button
							@click="logout"
							class="w-24 hover:bg-ihh-gray/10 group-hover:border-ihh-midnight-gray/50 text-center text-sm text-ihh-midnight-gray border rounded-sm border-ihh-gray/50 py-1.5"
							v-safe-html="`Sign Out`"
						/>
					</Login>
				</div>
				<div v-else>
					<button
						v-if="!showCustomerDetails"
						@click="showCustomerDetails = true"
						class="w-24 hover:bg-ihh-gray/10 group-hover:border-ihh-midnight-gray/50 text-center text-sm text-ihh-midnight-gray border rounded-sm border-ihh-gray/50 py-1.5"
					>
						Edit
					</button>
				</div>
			</template>

			<template v-if="showCustomerDetailsSlot" v-slot:details>
				<CheckoutLogin :showCustomerDetails="showCustomerDetailsSlot" @customer-continue="handleCustomerContinue" />
			</template>
		</CheckoutDetails>

		<!-- Shipping Details -->
		<CheckoutDetails id="shippingDetails" title="Shipping" v-if="!isDigitalOnly" v-show="checkout.shippingSectionVisible">
			<template v-slot:button>
				<button
					@click="showShippingDetails = !showShippingDetails"
					v-safe-html="showShippingDetails ? 'Hide' : 'Edit'"
					class="w-24 hover:bg-ihh-gray/10 group-hover:border-ihh-midnight-gray/50 text-center text-sm text-ihh-midnight-gray border rounded-sm border-ihh-gray/50 py-1.5"
				></button>
			</template>

			<template v-slot:summary>
				<AddressSummary
					v-if="!showShippingDetails"
					:address="checkout.shipping.shippingAddress"
					:shippingMethod="checkout.shipping.selectedShipping"
					:type="'shipping'"
				/>
			</template>

			<template v-slot:details>
				<div v-show="showShippingDetails" class="my-8 w-full" :class="{ 'opacity-40': savingShippingAddress }">
					<ShippingAddressDetails
						:addresses="checkout.addresses"
						:selectedAddress="checkout.shipping.shippingAddress"
						:enableAutoComplete="googleMapsLoaded"
						:loadingShipping="loadingShipping"
						v-model:hideShippingAddressForm="hideShippingAddressForm"
						@saveNewAddress="updateSaveNewShippingAddress"
						@continue="handleShippingContinue"
						@addressSelected="handleAddressSelected"
						@shippingAddressSynced="handleShippingAddressSynced"
					/>
				</div>
			</template>
		</CheckoutDetails>

		<!-- Billing Details -->
		<CheckoutDetails id="billingDetails" title="Billing" v-show="checkout.billingSectionVisible">
			<template v-slot:button>
				<button
					@click="showBillingDetails = !showBillingDetails"
					v-safe-html="showBillingDetails ? 'Hide' : 'Edit'"
					class="w-24 hover:bg-ihh-gray/10 group-hover:border-ihh-midnight-gray/50 text-center text-sm text-ihh-midnight-gray border rounded-sm border-ihh-gray/50 py-1.5"
				></button>
			</template>

			<template v-slot:summary>
				<AddressSummary :address="checkout.billing.billingAddress" v-if="!showBillingDetails" />
			</template>

			<template v-slot:details>
				<div v-show="showBillingDetails" :class="{ 'opacity-40': savingBillingAddress }">
					<BillingAddressDetails
						:addresses="checkout.addresses"
						:selectedAddress="checkout.billing.billingAddress"
						:enableAutoComplete="googleMapsLoaded"
						:showBillingDetails="showBillingDetails"
						@saveNewAddress="updateSaveNewBillingAddress"
						@continue="handleBillingContinue"
					/>
				</div>
			</template>
		</CheckoutDetails>

		<!-- International Shipping Disclaimer Modal -->
		<Modal maxWidth="max-w-3xl" :active="showInternationalDisclaimer" @hide="showInternationalDisclaimer = false" :warning="true" :hideX="true">
			<template v-slot>
				<div class="space-y-3 text-ihh-warning-text">
					<!-- underline offset on this line -->
					<div class="flex w-full justify-center text-ihh-warning-text font-semibold text-xl sm:text-2xl">International Shipping Notice</div>
					<div>
						While we make every effort to ensure delivery in a timely manner we
						<span class="ml-1 font-semibold underline underline-offset-2">cannot guarantee delivery of any order shipped outside the United States.</span>
					</div>
					<div>
						UPS shipping services are generally more reliable than USPS services and provide better tracking. The recipient is also responsible for any
						customs/duties fees due. Additionally, we also do not accept returns on international orders. Please
						<router-link to="/help-center" class="text-ihh-warning-link underline">contact us</router-link> if you have any questions and thank you for your
						understanding.
					</div>
					<div class="flex w-full justify-center">
						<button @click="showInternationalDisclaimer = false" class="bg-ihh-warning-text text-white py-2 px-4 rounded">I Understand</button>
					</div>
				</div>
			</template>
		</Modal>
	</div>
</template>

<script>
// Libraries
import Swal from 'sweetalert2'

// Components
import CheckoutDetails from '../pages/ihh-items/checkout-details.vue'
import CheckoutLogin from './checkout-login.vue'
import Login from '../layout/login.vue'
import AddressSummary from './address-summary.vue'
import ShippingAddressDetails from './shipping-address-details.vue'
import { useCheckoutStore, CheckoutFlowStep, hashShippingAddress } from '../../stores/store.js'
import BillingAddressDetails from './billing-address-details.vue'
import ShippingMethods from './shipping-methods.vue'

// Helpers
import helpers from '../../helpers/checkout.js'
import Mango from '../../helpers/mango.js'
import { loadGoogleMapsScript } from '../../helpers/googleMaps.js'
import { captureAddressError, captureCheckoutError, captureShippingError, CheckoutAction } from '../../helpers/sentry'

export default {
	name: 'ShippingAndBilling',

	emits: ['showPayment'],

	components: {
		CheckoutDetails,
		Login,
		CheckoutLogin,
		AddressSummary,
		BillingAddressDetails,
		ShippingAddressDetails,
		ShippingMethods,
	},
	inject: ['store'],

	props: {
		isDigitalOnly: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			checkout: useCheckoutStore(),
			// UI Variables
			loadingCheckout: false,
			disableCheckout: false,
			loadingShipping: false,

			showCustomerDetails: false,
			showShippingDetails: false,
			showBillingDetails: false,

			savingShippingAddress: false,
			savingBillingAddress: false,

			showInternationalDisclaimer: false,

			hideShippingAddressForm: false,

			// Address Data
			saveNewShippingAddress: false,
			saveNewBillingAddress: false,

			// Order Data
			orderComments: '',

			// Google Maps (loaded on demand when address sections open)
			googleMapsLoaded: false,
			googleMapsLoadPromise: null,
		}
	},

	async mounted() {
		window.addEventListener('checkout-session-ready', this.handleCheckoutSessionReady)
		this.loadingCheckout = true

		try {
			if (!this.isDigitalOnly) {
				if (!this.checkout.shipping.selectedShipping?.id) {
					// Only wipe address + quotes when nothing valid is saved. Clearing quotes on return
					// while the address remains caused an empty "enter your address" methods state.
					if (!this.checkout.shippingAddressValidated.value) {
						this.checkout.resetShipping()
					}

					if (this.isLoggedIn) {
						await this.loadCheckoutData()
					} else if (!this.checkout.guest?.email) {
						this.showCustomerDetails = true
					} else {
						this.showShippingDetails = true
					}
				}
			} else if (this.isDigitalOnly && this.isLoggedIn) {
				await this.fetchBillingAdressesOnly()
			}

			this.validateBillingMatchesShipping()
			this.restoreCheckoutProgressFromStore()

			if (this.showShippingDetails || this.showBillingDetails) {
				await this.ensureGoogleMapsLoaded()
			}
		} finally {
			this.loadingCheckout = false
		}
	},

	beforeUnmount() {
		window.removeEventListener('checkout-session-ready', this.handleCheckoutSessionReady)
	},

	methods: {
		async handleCheckoutSessionReady() {
			this.showCustomerDetails = false
			if (!this.isLoggedIn || this.isDigitalOnly) {
				this.checkout.updateLoading(false)
				return
			}
			this.loadingCheckout = true
			this.checkout.updateLoading(true)
			try {
				// loadCheckoutData already calls restoreCheckoutProgressFromStore in its finally block
				await this.loadCheckoutData({ openShippingDetails: false })
			} finally {
				this.loadingCheckout = false
				this.checkout.updateLoading(false)
			}
		},
		// Customer Related
		handleCustomerContinue(email) {
			this.checkout.updateGuestEmail((email || '').trim())
			if (!this.checkout.emailValidated) {
				this.showCustomerDetails = true
				return
			}

			console.log('customer added email to checkout: ', this.checkout.guest.email)
			this.showCustomerDetails = false

			if (this.checkout.isDigitalOnly) {
				this.checkout.advanceFlowStep(CheckoutFlowStep.BILLING)
				this.showBillingDetails = true
			} else {
				this.checkout.advanceFlowStep(CheckoutFlowStep.SHIPPING)
				this.showShippingDetails = true
			}
		},

		// Shipping Related
		async handleAddressSelected(syncMeta = {}) {
			if (this.checkout.shipping.billingMatchesShipping) this.checkout.copyShippingToBilling()
			this.loadingShipping = true
			await this.getShippingOptions(syncMeta)
		},
		async handleShippingAddressSynced(syncMeta = {}) {
			// Debounced form commit: refresh BC consignment without requiring Continue click
			if (!syncMeta?.needsShippingRefetch) return
			await this.handleAddressSelected(syncMeta)
		},
		async handleShippingContinue() {
			if (this.saveNewShippingAddress) {
				this.savingShippingAddress = true

				const address = await this.saveNewAddress('shipping')
				if (address?.id) this.checkout.addressesPush(address)

				this.savingShippingAddress = false
			}

			if (this.checkout.showShippingDisclaimer) {
				this.showInternationalDisclaimer = true
			}

			this.checkout.advanceFlowStep(CheckoutFlowStep.BILLING)
			this.showShippingDetails = false

			if (this.checkout.shipping.billingMatchesShipping) this.checkout.copyShippingToBilling()

			if (!this.checkout.billingAddressValidated.value) {
				this.showBillingDetails = true
			} else {
				this.checkout.advanceFlowStep(CheckoutFlowStep.PAYMENT)
				this.$emit('showPayment')
			}
			this.$nextTick(() => {
				// Scroll to the top of the billing section
				document.getElementById('customerDetails').scrollIntoView({ behavior: 'smooth', block: 'start' })
			})
		},
		updateSaveNewShippingAddress(value) {
			this.saveNewShippingAddress = value
		},
		async ensureBcCartBeforeShippingQuotes() {
			const hasItems = this.store?.cart?.cartItems?.length || this.store?.cart?.certificates?.length
			if (!hasItems || !this.store?.user?.id) return
			if (this.store.cart?.bcId) return
			if (typeof this.store.saveCart === 'function') {
				await this.store.saveCart()
			}
		},
		async getShippingOptions(syncMeta = {}) {
			const { previousMethodId = null } = syncMeta
			try {
				await this.ensureBcCartBeforeShippingQuotes()
				await helpers.getShippingEstimates()
				if (previousMethodId && this.checkout.shipping.shippingOptions?.length) {
					const match = this.checkout.shipping.shippingOptions.find((option) => option.id === previousMethodId)
					if (match) {
						this.checkout.selectShipping(match)
						await helpers.selectShippingMethod()
					}
				}
			} catch (error) {
				console.error('Error fetching shipping options:', error)
				captureShippingError(error, {
					action: CheckoutAction.GET_SHIPPING_ESTIMATES,
					store: this.store,
					checkout: this.checkout,
					extra: { phase: 'getShippingOptions', previousMethodId },
				})
				this.checkout.setShippingOptions([])
			} finally {
				this.loadingShipping = false
			}
		},

		async recoverFromMissingConsignment() {
			// Called when validateConsignment fails at payment time
			// Auto-fetch shipping options using existing address data
			this.showShippingDetails = true
			this.loadingShipping = true

			// If we have a shipping address, auto-fetch shipping options and re-sync BC
			if (this.checkout.shipping.shippingAddress?.address1) {
				const previousMethodId = this.checkout.shipping.selectedShipping?.id || null
				await this.getShippingOptions({ previousMethodId })
				if (this.checkout.shipping.selectedShipping?.id) {
					await helpers.selectShippingMethod()
				}
			} else {
				this.loadingShipping = false
			}

			// Scroll to shipping section after options are loaded and DOM is updated
			this.$nextTick(() => {
				setTimeout(() => {
					document.getElementById('shippingDetails')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
				}, 100)
			})
		},

		// Billing Related
		async handleBillingContinue() {
			if (this.saveNewBillingAddress) {
				this.savingBillingAddress = true

				const address = await this.saveNewAddress('billing')
				if (address?.id) this.checkout.addressesPush(address)

				this.savingBillingAddress = false
			}
			this.checkout.advanceFlowStep(CheckoutFlowStep.PAYMENT)
			this.showBillingDetails = false
			this.$emit('showPayment')

			this.$nextTick(() => {
				// Scroll to the top of the billing section
				document.getElementById('customerDetails').scrollIntoView({ behavior: 'smooth', block: 'start' })
			})
		},
		updateSaveNewBillingAddress(value) {
			this.saveNewBillingAddress = value
		},
		validateBillingMatchesShipping() {
			if (this.checkout.shipping.billingMatchesShipping && this.checkout.shippingAddressValidated.value && !this.checkout.billingAddressValidated.value) {
				this.checkout.updateBillingMatchesShipping(false)
			}
		},

		/**
		 * Re-open billing/payment sections when Pinia + sessionStorage already hold a complete
		 * checkout (e.g. user left checkout and returned). Local step flags reset on remount.
		 */
		async restoreCheckoutProgressFromStore() {
			const customerReady = this.isLoggedIn || !!this.checkout.guest?.email
			if (!customerReady) return

			if (this.checkout.shipping.shippingAddress?.address1 && !this.checkout.shipping.lastAddressHash) {
				this.checkout.setLastAddressHash(hashShippingAddress(this.checkout.shipping.shippingAddress))
			}

			this.checkout.restoreFlowStepFromStore()

			if (this.isDigitalOnly) {
				if (this.checkout.isFlowStepAtLeast(CheckoutFlowStep.PAYMENT)) {
					this.showBillingDetails = false
					this.$nextTick(() => this.$emit('showPayment'))
				} else if (this.checkout.isFlowStepAtLeast(CheckoutFlowStep.BILLING)) {
					this.showBillingDetails = true
				}
				return
			}

			// Address saved but no method yet (left checkout mid-shipping step)
			if (this.checkout.flowStep === CheckoutFlowStep.SHIPPING && this.checkout.shippingAddressValidated.value && !this.checkout.shippingMethodSelected) {
				this.showShippingDetails = true
				const needsQuotes =
					!this.checkout.shipping.shippingOptions?.length || !this.checkout.shipping.consignmentId
				if (this.checkout.shipping.shippingAddress?.address1 && needsQuotes) {
					this.loadingShipping = true
					return this.getShippingOptions()
				}
				return
			}

			if (!this.checkout.shippingStepComplete) return

			this.showShippingDetails = false

			if (this.checkout.shipping.shippingAddress?.address1 && !this.checkout.shipping.consignmentId) {
				this.loadingShipping = true
				return this.getShippingOptions({ previousMethodId: this.checkout.shipping.selectedShipping?.id || null })
			}

			if (this.checkout.isFlowStepAtLeast(CheckoutFlowStep.PAYMENT)) {
				this.showBillingDetails = false
				this.$nextTick(() => this.$emit('showPayment'))
			} else if (this.checkout.isFlowStepAtLeast(CheckoutFlowStep.BILLING)) {
				this.showBillingDetails = true
			}
		},

		// General
		async ensureGoogleMapsLoaded() {
			if (this.googleMapsLoaded) return true

			if (!this.googleMapsLoadPromise) {
				this.googleMapsLoadPromise = loadGoogleMapsScript()
					.then(() => {
						this.googleMapsLoaded = true
						return true
					})
					.catch((error) => {
						console.error('Error loading Google Maps script:', error)
						this.googleMapsLoadPromise = null
						return false
					})
			}

			return this.googleMapsLoadPromise
		},
		async loadCheckoutData({ openShippingDetails = true } = {}) {
			this.loadingCheckout = true
			this.loadingShipping = true

			try {
				await this.fetchAddresses({ openShippingDetails })
			} catch (error) {
				// fetchAddresses has its own error handling, but catch any unexpected errors
				console.error('Unexpected error in loadCheckoutData:', error)
				captureCheckoutError(error, {
					component: 'ShippingAndBilling',
					action: 'loadCheckoutData',
					store: this.store,
					checkout: this.checkout,
				})
			} finally {
				this.loadingShipping = false
				await this.restoreCheckoutProgressFromStore()
			}
		},
		async fetchBillingAdressesOnly() {
			try {
				const addresses = await Mango.addresses({ limit: 9999, search: { author: this.store?.user?.id } })

				for (let address of addresses) {
					delete address.author
				}

				this.checkout.initializeBillingAddresses(addresses)
				console.log('billing addresses fetched')
			} catch (error) {
				console.error('Error fetching billing addresses:', error)
				captureAddressError(error, {
					action: CheckoutAction.FETCH_BILLING_ADDRESSES,
					addressType: 'billing',
					store: this.store,
					checkout: this.checkout,
				})
				// Initialize with empty addresses so user can still add new ones
				this.checkout.initializeBillingAddresses([])
				Swal.fire({
					icon: 'warning',
					title: 'Could not load saved addresses',
					text: 'You can still enter a new billing address to continue.',
					confirmButtonText: 'OK',
				})
			} finally {
				this.showBillingDetails = true
			}
		},
		async fetchAddresses({ openShippingDetails = true } = {}) {
			try {
				const addresses = await Mango.addresses({ limit: 9999, search: { author: this.store?.user?.id } })

				for (let address of addresses) {
					delete address.author
				}
				this.checkout.initializeAddresses(addresses)
				console.log('addresses fetched')

				await this.getShippingOptions()

				if (openShippingDetails) {
					this.hideShippingAddressForm = true
					this.showShippingDetails = true
					await this.ensureGoogleMapsLoaded()
				}
			} catch (error) {
				console.error('Error fetching addresses:', error)
				captureAddressError(error, {
					action: CheckoutAction.FETCH_ADDRESSES,
					addressType: 'shipping',
					store: this.store,
					checkout: this.checkout,
				})
				// Initialize with empty addresses so user can still add new ones
				this.checkout.initializeAddresses([])
				if (openShippingDetails) {
					this.showShippingDetails = true
				}
				Swal.fire({
					icon: 'warning',
					title: 'Could not load saved addresses',
					text: 'You can still enter a new address to continue.',
					confirmButtonText: 'OK',
				})
			}
		},
		async saveNewAddress(type) {
			console.log('saving new address to Mango....')

			let address
			if (type === 'shipping') {
				address = {
					...this.checkout.shipping.shippingAddress,
					bcCustomerId: this.store.user?.bcId,
				}
			} else {
				address = {
					...this.checkout.billing.billingAddress,
					bcCustomerId: this.store.user?.bcId,
				}
			}

			try {
				let response = await Mango.addresses.save(address, false, true)

				if (response?.response?.id && !response.warnings) {
					return response.response
				} else {
					console.log(response)
					captureAddressError(`Failed to save ${type} address: ${response?.warnings?.join(', ') || 'Unknown error'}`, {
						action: CheckoutAction.SAVE_ADDRESS,
						addressType: type,
						store: this.store,
						checkout: this.checkout,
						extra: {
							warnings: response?.warnings || [],
							address: { city: address?.city, stateOrProvince: address?.stateOrProvince, countryCode: address?.countryCode },
						},
					})
					Swal.fire({ text: `Error saving address: ${response?.warnings?.join(', ') || 'Unknown error'}`, icon: 'error' })
					return null
				}
			} catch (error) {
				console.error('Error saving address:', error)
				captureAddressError(error, {
					action: CheckoutAction.SAVE_ADDRESS,
					addressType: type,
					store: this.store,
					checkout: this.checkout,
					extra: {
						address: { city: address?.city, stateOrProvince: address?.stateOrProvince, countryCode: address?.countryCode },
					},
				})
				Swal.fire({ text: 'Error saving address. Please try again.', icon: 'error' })
				return null
			}
		},
		hashAddress(address) {
			if (!address) return ''
			return JSON.stringify({
				address1: address.address1,
				address2: address.address2,
				city: address.city,
				stateOrProvince: address.stateOrProvince,
				postalCode: address.postalCode,
				countryCode: address.countryCode,
			})
		},
	},

	computed: {
		isSectionLoading() {
			return this.loadingCheckout || this.checkout.loadingCheckout
		},
		isLoggedIn() {
			return !!this.store?.user?.id
		},
		customerComplete() {
			return !this.showCustomerDetailsSlot && this.checkout.customerStepComplete
		},
		showCustomerDetailsSlot() {
			if (this.showCustomerDetails) return true
			if (this.isLoggedIn && !this.store?.user?.email) {
				return true
			}
			if (this.isLoggedIn && !!this.store?.user?.email) {
				return false
			}
			// Guest: keep the form open until Continue advances the flow step
			return !this.checkout.customerStepComplete
		},
	},

	watch: {
		showShippingDetails: {
			handler(visible) {
				if (visible) this.ensureGoogleMapsLoaded()
			},
		},
		showBillingDetails: {
			handler(visible) {
				if (visible) this.ensureGoogleMapsLoaded()
			},
		},
		'store.cart.bcId': {
			handler(newVal, oldVal) {
				if (newVal && this.store?.user?.id && newVal !== oldVal) {
					this.loadCheckoutData({ openShippingDetails: !this.checkout.shippingStepComplete })
				} else if (!newVal) {
					this.loadingCheckout = false
				}
			},
			immediate: false,
		},
		'store.user.id': {
			handler(newVal) {
				if (newVal) {
					this.showCustomerDetails = false
				}
			},
			immediate: false,
		},
	},
}
</script>

<style scoped></style>
